import { SyntheticEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useKeycloak } from '@react-keycloak/web';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import moment, { Moment } from 'moment';
import { setErrorFeedback, setSuccessFeedback } from '../../../../features/feedback/feedbackSlice';
import {
  selectUnidadesDeNegocioAssociadas,
  selectUnidadeSelecionada,
} from '../../../../features/usuario-logado/usuarioLogadoSlice';
import {
  fetchAllAsync as fetchEmpresasEstrangeiras,
  selectEmpresasEstrangeiras,
} from '../../../../features/empresa-estrangeira/empresaEstrangeiraSlice';
import { GridItemModalProps } from '../../../common/components/cardsGrid/cardsModalGrid';
import SelectStyled from '../../../common/components/input/selectStyled';
import MultiSelectStyled from '../../../common/components/input/multiSelectStyled';
import QCXDatePicker from '../../../../shared-components/date-picker/QCXDatePicker';
import generateRelatorioCatalogoProdutos from './relatorioCatalogoProdutosPage.helpers';

interface ControlledValue {
  id: string | number;
  value: string;
}

interface IUnidadeDeNegocio {
  id: number;
  pessoa: { nome: string; nomeResumido: string };
}

const useStyles = makeStyles({
  root: {
    '& .MuiDialog-paper': {
      minWidth: '52vw',
    },
  },
});

const RelatorioCatalogoProdutosPage = ({ title, isOpen, closeModal }: GridItemModalProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { keycloak } = useKeycloak();
  const { token } = keycloak;

  const selectedUnit = useSelector(selectUnidadeSelecionada);
  const businessUnits: IUnidadeDeNegocio[] = useSelector(selectUnidadesDeNegocioAssociadas);
  const empresasEstrangeiras = useSelector(selectEmpresasEstrangeiras);

  const [selectedBusiness, setSelectedBusiness] = useState<ControlledValue | undefined>(undefined);
  const [selectedFabricantes, setSelectedFabricantes] = useState<ControlledValue[]>([]);
  const [partnumber, setPartnumber] = useState('');
  const [dataInicio, setDataInicio] = useState<Moment | null>(null);
  const [dataFim, setDataFim] = useState<Moment | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchEmpresasEstrangeiras());
  }, [dispatch]);

  const tenantId = selectedBusiness ? Number(selectedBusiness.id) : selectedUnit;

  const handleBusinessChange = (
    _event: SyntheticEvent,
    value: { id: string | number | undefined; value: string | undefined } | null
  ) => {
    if (value == null) setSelectedBusiness(undefined);
    else setSelectedBusiness({ id: value.id as string, value: value.value as string });
  };

  const handleFabricantesChange = (_event: SyntheticEvent, value: ControlledValue[] | null) => {
    setSelectedFabricantes(value || []);
  };

  const handleSubmit = async () => {
    if (!token || !tenantId) {
      dispatch(setErrorFeedback({ message: 'Selecione uma unidade de negócio.' }));
      return;
    }

    setLoading(true);
    keycloak.updateToken(300);

    try {
      const fabricanteIds = selectedFabricantes.map((f) => String(f.id));
      const error = await generateRelatorioCatalogoProdutos(
        token,
        tenantId,
        partnumber || undefined,
        dataInicio ? dataInicio.format('YYYY-MM-DD') : undefined,
        dataFim ? dataFim.format('YYYY-MM-DD') : undefined,
        fabricanteIds.length > 0 ? fabricanteIds : undefined
      );
      if (error) {
        dispatch(setErrorFeedback({ message: error }));
      } else {
        dispatch(setSuccessFeedback({ message: 'Relatório gerado com sucesso!' }));
        closeModal();
      }
    } catch {
      dispatch(setErrorFeedback({ message: 'Erro inesperado ao gerar o relatório.' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog className={classes.root} open={isOpen} onClose={closeModal}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <SelectStyled
                disablePortal={false}
                controlledValue={selectedBusiness}
                options={businessUnits.map((bu) => ({
                  id: bu.id,
                  value: bu.pessoa.nomeResumido ?? bu.pessoa.nome,
                }))}
                onChangeAction={handleBusinessChange}
                label="Unidade de Negócio"
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              label="Part Number"
              value={partnumber}
              onChange={(e) => setPartnumber(e.target.value)}
              size="small"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <MultiSelectStyled
                disablePortal={false}
                options={empresasEstrangeiras.map((e: any) => {
                  const nome = e.pessoaJuridica?.nomeResumido || e.pessoaJuridica?.nome || String(e.id);
                  return { id: e.id, value: nome };
                })}
                onChangeAction={handleFabricantesChange}
                label="Fabricante"
              />
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <QCXDatePicker
              label="Data de Início"
              value={dataInicio}
              onChange={setDataInicio}
            />
          </Grid>

          <Grid item xs={6}>
            <QCXDatePicker
              label="Data de Fim"
              value={dataFim}
              onChange={setDataFim}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Box padding={2} display="flex" gridGap={8}>
          <Button variant="outlined" color="primary" onClick={closeModal} disabled={loading}>
            Fechar
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmit}
            disabled={loading || !tenantId}
          >
            {loading ? 'Gerando...' : 'Gerar Relatório'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default RelatorioCatalogoProdutosPage;
